import { useEffect, useRef } from 'react';
import { Button } from 'common/components/Button';
import { useHeroIntroAnimation } from 'common/hook/useHeroIntroAnimation';
import { EmployeeProfileData } from 'common/types/employee';
import gsap from 'gsap';
import Image from 'next/image';
import useDimensions from 'use-element-dimensions';

export const EmployeeProfileHero: React.FC<{ employee: EmployeeProfileData }> = ({ employee }) => {
  const el = useRef<HTMLElement | null>(null);
  const [_containerWidth, ref] = useDimensions(entry => entry.contentRect.width);

  useHeroIntroAnimation({
    scopeRef: el,
    revealAnimations: [],
  });

  useEffect(() => {
    gsap.set('.employee-hero-fade', { opacity: 0, y: 24 });
    gsap.to('.employee-hero-fade', {
      opacity: 1,
      y: 0,
      duration: 0.7,
      stagger: 0.18,
      delay: 1.2,
      ease: 'power2.out',
    });

    gsap.set('#hero-details-section', { opacity: 0, y: 40 });
    gsap.to('#hero-details-section', {
      opacity: 1,
      y: 0,
      duration: 0.5,
      delay: 2.4,
      ease: 'power3.out',
    });
  }, []);

  return (
    <section ref={el} className="hero-bg-img flex min-h-[110vh] flex-col justify-between bg-white">
      <div className="container relative py-24 pb-32 md:py-32 h-full">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)] lg:items-end h-full">
          <div className="h-full h-full flex flex-col justify-center" ref={ref}>
            <h1
              id="hero-title"
              className="text-heading-hero max-w-[90vw] lg:max-w-[860px]"
              dangerouslySetInnerHTML={{ __html: employee.hero.title }}
            />
            <p className="mt-8 max-w-[62ch] text-black employee-hero-fade">
              {employee.hero.subtitle}
            </p>
            <p className="mt-6 max-w-[66ch] text-black employee-hero-fade">
              {employee.credibilityHint}
            </p>

            <div className="mt-10 flex flex-col gap-4 md:flex-row md:flex-wrap employee-hero-fade">
              <Button href={employee.consultingCtaHref} rel="noopener noreferrer" target="_blank">
                {employee.dualCta.person.cta}
              </Button>
              <Button href={employee.companyCtaHref} type="outline-dark">
                {employee.dualCta.company.cta}
              </Button>
            </div>
          </div>

          <div
            id="hero-details-section"
            className="overflow-hidden rounded-[28px] border border-white/10 bg-gradient p-4 lg:p-8">
            <div className="relative mb-6 overflow-hidden rounded-[22px] border border-white/10 bg-[#140f1d]">
              <Image
                alt={employee.hero.image.alt}
                className="h-[460px] w-full object-cover object-top"
                height={employee.hero.image.height}
                loading="eager"
                priority
                src={employee.hero.image.src}
                width={employee.hero.image.width}
              />
            </div>
            <div className="text-primary text-5xl">{employee.name}</div>
            <div className="text-white">{employee.role}</div>
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {employee.stats.map(item => (
                <div
                  key={item.label}
                  className="rounded-[22px] border border-white/10 bg-[#140f1d] p-5">
                  <div className="text-outline-primary text-4xl">{item.value}</div>
                  <div className="mt-2">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
